// Must stay the first import: it captures the unwrapped window.fetch before
// LogRocket.init() below instruments it (the chat SSE stream needs the native
// one, see nativeFetch.ts).
import './core/utils/logrocket/nativeFetch'
import * as Sentry from '@sentry/react'
import Bowser from 'bowser'
import { cloneDeepWith, contains, getOr, merge, pipe } from 'lodash/fp'
import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'
import posthog from 'posthog-js'
import { Userpilot } from 'userpilot'

import { eventTracker } from '@masterplandev/utils'

import { env } from '@/env'

import { SENTRY_SECTIONS, TRACK_EXIT } from './core/constants/constants'
import requestSanitizer from './core/utils/logrocket/requestSanitizer'
import trackExit from './core/utils/tracking/trackExit'
import { router } from './router/router'

const {
  APP_VERSION,
  REACT_APP_DOMAIN,
  REACT_APP_LOGROCKET_KEY,
  REACT_APP_SENTRY_DSN,
  MODE,
  REACT_APP_CIRCLE_SHA1,
  REACT_APP_SUBDOMAIN,
  REACT_APP_API_SUBDOMAIN,
  REACT_APP_API_URL_PREFIX,
  REACT_APP_USERPILOT_TOKEN,
  REACT_APP_MIXPANEL_KEY,
  REACT_APP_MIXPANEL_DEBUG,
  REACT_APP_PUBLIC_POSTHOG_KEY,
  REACT_APP_PUBLIC_POSTHOG_API_HOST,
  REACT_APP_PUBLIC_POSTHOG_UI_HOST,
} = env

/**
 * Vidstack calls `exitFullscreen()` from `onDestroy` without awaiting it. That
 * method is `async` and throws straight away when the player no longer has a
 * fullscreen adapter, so the rejection escapes vidstack's own `try/catch` and
 * reaches the browser as an unhandled rejection. It happens while the player is
 * being torn down, after the user has already left the lecture, so it is pure
 * reporting noise.
 *
 * Every reported occurrence of this message has come from that teardown path,
 * so it is matched on the message alone rather than on a stack frame, which
 * would depend on how the production bundle is minified.
 */
const VIDSTACK_FULLSCREEN_UNSUPPORTED = '[vidstack] no fullscreen support'

/**
 * Vidstack defers its internal end handler by a macrotask
 * (`setTimeout(() => this.#onEnded(event), 0)`). Our own `onEnd` handler moves
 * the lecture out of `inProgress`, so React unmounts the player and vidstack
 * nulls `$state` before that callback runs, and the handler then destructures
 * null. Progress and tracking have already been saved at that point, and the
 * player is being destroyed anyway, so this is pure reporting noise.
 *
 * The production stack trace only contains hashed bundle chunk URLs here, so
 * vidstack frames cannot be recognised and the message is all we have. Engines
 * word the failure differently, hence the list. Samples on
 * https://masterplan-com-gmbh.sentry.io/issues/5909909599/ all come from this
 * teardown path.
 */
const VIDSTACK_TEARDOWN_ERRORS = [
  // Chromium
  "Cannot destructure property 'paused' of 'this.$state' as it is null.",
  // Firefox
  'this.$state is null',
  // Safari
  "Cannot destructure property 'paused' from null or undefined value",
]

const shouldIgnoreError = (error) => {
  if (error && contains('https://fast.wistia.com', error.stack)) {
    return true
  }
  if (error?.message === VIDSTACK_FULLSCREEN_UNSUPPORTED) {
    return true
  }
  if (VIDSTACK_TEARDOWN_ERRORS.includes(error?.message)) {
    return true
  }
  return false
}

const IS_E2E = typeof window !== 'undefined' && !!(window as any).Cypress
const reportedSchemaValidationContexts = new Set<string>()

if (REACT_APP_MIXPANEL_KEY && !IS_E2E) {
  const referringDomain = (referrer) => {
    const split = referrer.split('/')
    return split.length >= 3 ? split[2] : ''
  }
  const { browser, os } = Bowser.parse(window.navigator.userAgent)
  const { width, height } = window.screen
  const referrer = getOr('$direct', 'referrer', document)
  eventTracker.set_config({ debug: REACT_APP_MIXPANEL_DEBUG })
  eventTracker.on_error((error: any) => {
    if (error?.message !== 'Network Error') {
      Sentry.captureException(error, {
        level: 'error',
        tags: { section: SENTRY_SECTIONS.eventTracking },
      })
    }
  })
  eventTracker.register({
    mp_lib: 'web',
    $os: os.name,
    $browser: browser.name,
    $browser_version: parseInt(browser.version || '0', 10),
    $initial_referrer: referrer,
    $initial_referring_domain: referringDomain(referrer),
    $screen_height: height,
    $screen_width: width,
  })
  eventTracker.time_event(TRACK_EXIT)
  eventTracker.init(REACT_APP_MIXPANEL_KEY)

  window.addEventListener('beforeunload', () => trackExit())
}

if (REACT_APP_LOGROCKET_KEY && !IS_E2E) {
  LogRocket.init(REACT_APP_LOGROCKET_KEY, {
    network: { requestSanitizer },
    // inputSanitizer is a valid LogRocket option but absent from their v0.6.x type definitions
    dom: { inputSanitizer: true } as {
      isEnabled?: boolean
      inputSanitizer?: boolean
    },
    shouldCaptureIP: false,
  })
  LogRocket.getSessionURL((sessionURL) => {
    eventTracker.register({ sessionURL })
  })
  setupLogRocketReact(LogRocket)
}

if (window.newrelic) {
  window.newrelic.setErrorHandler(shouldIgnoreError)
  window.addEventListener('unhandledrejection', (event) => {
    window.newrelic?.noticeError((event as any)?.detail?.reason?.error, {
      event,
    })
  })
}

if (REACT_APP_SENTRY_DSN) {
  const apiBaseUrl = `${window.location.origin.replace(
    `https://${REACT_APP_SUBDOMAIN}.`,
    `https://${REACT_APP_API_SUBDOMAIN}.`,
  )}${REACT_APP_API_URL_PREFIX}`

  Sentry.init({
    dsn: REACT_APP_SENTRY_DSN,
    enabled: !IS_E2E,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        block: ['[data-private]'],
        networkDetailAllowUrls: [window.location.origin],
      }),
    ],
    tracePropagationTargets: [apiBaseUrl],
    environment: MODE,
    release: REACT_APP_CIRCLE_SHA1,
    tracesSampleRate: MODE === 'production' ? 0.25 : 1.0,
    replaysSessionSampleRate: MODE === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: (event, hint) => {
      if (shouldIgnoreError(hint?.originalException)) {
        return null
      }

      if (
        MODE === 'production' &&
        event.tags?.section === SENTRY_SECTIONS.schemaValidation
      ) {
        const context = String(event.tags.context ?? 'unknown')

        if (reportedSchemaValidationContexts.has(context)) {
          if (Math.random() >= 0.01) {
            return null
          }
        } else {
          reportedSchemaValidationContexts.add(context)
        }
      }

      const removeCertificateContent = cloneDeepWith((value) =>
        contains('<title>Certificate</title>', value)
          ? '[Certificate Content]'
          : undefined,
      )

      const addLogRocketURL = merge({
        extra: { sessionURL: LogRocket.sessionURL },
      })

      return pipe(addLogRocketURL, removeCertificateContent)(event)
    },
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // newrelic
      /newrelic\.com\//i,
      // logrocket
      /logrocket\.io\//i,
    ],
  })

  Sentry.setContext('Environment Variables', {
    MODE,
    APP_VERSION,
    REACT_APP_DOMAIN,
    REACT_APP_API_URL_PREFIX,
  })
}

if (REACT_APP_USERPILOT_TOKEN && !IS_E2E) {
  Userpilot.initialize(REACT_APP_USERPILOT_TOKEN)
}

if (REACT_APP_PUBLIC_POSTHOG_KEY && !IS_E2E) {
  posthog.init(REACT_APP_PUBLIC_POSTHOG_KEY, {
    api_host: REACT_APP_PUBLIC_POSTHOG_API_HOST,
    ui_host: REACT_APP_PUBLIC_POSTHOG_UI_HOST,
    capture_pageview: false,
    persistence: 'localStorage+cookie',
  })
}
